import React, {
  memo,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";

import {
  Box,
  Button,
  IconButton,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
} from "@mui/material";

import StorybookCover from "./StorybookCover";
import { STORYBOOK_FAQ_ITEMS } from "../components/StorybookFAQ";

const noop = () => {};

function finiteNumber(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function StorybookDrawer({
  open = false,
  onClose = noop,

  event,
  coverSrcOverride,

  storybookShowFaq,
  setStorybookShowFaq,

  storybookLabelCurrent = 0,
  storybookTarget = 50,
  storybookPct,
  storybookLocked,

  sbCheckoutLoading = false,
  onCheckout,

  coverImageUrl = "",
  eventTitle,
  eventDateLabel,
  coverPhotoPosition = "center",

  priceLabel = "",
  shippingLabel =
    "Ships within the US only. Shipping and tax calculated at checkout.",
}) {
  const titleId = useId();
  const reduceMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );

  const scrollRef = useRef(null);
  const checkoutInFlight = useRef(false);

  const [entered, setEntered] = useState(false);
  const [localFaq, setLocalFaq] = useState(false);
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const customization = event?.customization || {};

  const resolvedImage =
    (typeof coverSrcOverride === "string" &&
      coverSrcOverride) ||
    customization.coverPhotoUrl ||
    customization.coverPhotoDataUrl ||
    coverImageUrl ||
    "";

  const resolvedTitle =
    eventTitle ||
    customization.eventTitle ||
    event?.details?.eventName ||
    event?.eventName ||
    event?.name ||
    "Your memories";

  const resolvedDate =
    eventDateLabel ||
    event?.eventDateLabel ||
    event?.details?.eventDateLabel ||
    customization.eventDateLabel ||
    event?.details?.eventDate ||
    event?.eventDate ||
    customization.eventDate ||
    "";

  const controlledFaq =
    typeof storybookShowFaq === "boolean" &&
    typeof setStorybookShowFaq === "function";

  const showFaq = controlledFaq
    ? storybookShowFaq
    : localFaq;

  const current = Math.max(
    0,
    finiteNumber(storybookLabelCurrent, 0)
  );

  const target = Math.max(
    1,
    finiteNumber(storybookTarget, 50)
  );

  const percentage = Math.max(
    0,
    Math.min(
      100,
      finiteNumber(storybookPct, (current / target) * 100)
    )
  );

  const locked =
    typeof storybookLocked === "boolean"
      ? storybookLocked
      : current < target;

  const busy = Boolean(
    sbCheckoutLoading || checkoutPending
  );

  const toggleFaq = useCallback(() => {
    if (controlledFaq) {
      setStorybookShowFaq(!showFaq);
    } else {
      setLocalFaq(!showFaq);
    }

    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [
    controlledFaq,
    setStorybookShowFaq,
    showFaq,
  ]);

  const handleEnter = useCallback(() => {
    setEntered(false);
    setCheckoutError("");

    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, []);

  const handleCheckout = useCallback(async () => {
    if (
      locked ||
      busy ||
      checkoutInFlight.current ||
      typeof onCheckout !== "function"
    ) {
      return;
    }

    checkoutInFlight.current = true;
    setCheckoutPending(true);
    setCheckoutError("");

    try {
      await onCheckout();
    } catch {
      setCheckoutError(
        "Checkout couldn't open. Please try again."
      );
    } finally {
      checkoutInFlight.current = false;
      setCheckoutPending(false);
    }
  }, [locked, busy, onCheckout]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      variant="temporary"
      open={open}
      onOpen={noop}
      onClose={onClose}
      disableDiscovery
      disableSwipeToOpen
      disableBackdropTransition
      transitionDuration={
        reduceMotion
          ? 0
          : { enter: 180, exit: 140 }
      }
      ModalProps={{
        keepMounted: true,
      }}
      SlideProps={{
        onEnter: handleEnter,
        onEntered: () => setEntered(true),
        onExit: () => setEntered(false),
        onExited: () => setEntered(false),
      }}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": titleId,
        sx: {
          width: "100%",
          maxWidth: 600,
          mx: "auto",
          boxSizing: "border-box",
          height: "auto",
          maxHeight: {
            xs: "84vh",
            sm: "86vh",
          },
          "@supports (height: 1dvh)": {
            maxHeight: {
              xs: "84dvh",
              sm: "86dvh",
            },
          },
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          bgcolor: "#fff",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 -4px 20px rgba(0,0,0,.12)",
        },
      }}
    >
      {/* Drag handle */}
      <Box
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          height: 26,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 4,
            borderRadius: 999,
            bgcolor: "#d4d4d4",
          }}
        />
      </Box>

      {/* Equal side columns keep the title centered */}
      <Box
        sx={{
          flexShrink: 0,
          display: "grid",
          gridTemplateColumns: "44px minmax(0, 1fr) 44px",
          alignItems: "center",
          px: 1.5,
          pb: 1,
          borderBottom: "1px solid #efefef",
        }}
      >
        <Box aria-hidden="true" />

        <Typography
          id={titleId}
          component="h2"
          sx={{
            m: 0,
            textAlign: "center",
            fontSize: {
              xs: 17,
              sm: 18,
            },
            fontWeight: 700,
            lineHeight: 1.3,
            color: "#111",
          }}
        >
          {showFaq
            ? "Photo book FAQ"
            : "Get your own photo book"}
        </Typography>

        <IconButton
          aria-label="Close photo book drawer"
          onClick={onClose}
          sx={{
            width: 44,
            height: 44,
            color: "#111",
          }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </IconButton>
      </Box>

      {/* Scrollable content */}
      <Box
        ref={scrollRef}
        sx={{
          flex: "0 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overscrollBehaviorY: "contain",
          WebkitOverflowScrolling: "touch",
          px: 2.5,
          pt: 2,
          pb: 2,
        }}
      >
        {showFaq ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {STORYBOOK_FAQ_ITEMS.map((item, index) => (
              <Box
                component="details"
                key={index}
                sx={{
                  border: "1px solid #e9e9e9",
                  borderRadius: 2,
                  p: 1.5,
                  bgcolor: "#fff",
                  "& > summary": {
                    cursor: "pointer",
                    minHeight: 30,
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.5,
                    color: "#111",
                  },
                  "& > summary:focus-visible": {
                    outline: "2px solid #111",
                    outlineOffset: 4,
                  },
                }}
              >
                <summary>{item.q}</summary>

                <Typography
                  component="div"
                  sx={{
                    pt: 1.25,
                    fontSize: 14,
                    color: "text.secondary",
                    lineHeight: 1.6,
                  }}
                >
                  {item.a}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: 1.5,
                color: "text.secondary",
              }}
            >
              This celebration, in a book you can keep.
            </Typography>

            <StorybookCover
              imageUrl={
                typeof resolvedImage === "string"
                  ? resolvedImage
                  : ""
              }
              eventTitle={String(resolvedTitle)}
              eventDateLabel={
                typeof resolvedDate === "string"
                  ? resolvedDate
                  : ""
              }
              photoPosition={coverPhotoPosition}
              loadPhoto={open && entered}
            />

            <Typography
              sx={{
                mt: 0.5,
                fontSize: 11,
                lineHeight: 1.4,
                color: "text.secondary",
              }}
            >
              Sample cover. Final design may vary.
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: 1.5,
                color: "#111",
                textWrap: "balance",
              }}
            >
              Made from this event&apos;s photos.
              Delivered to your door.
            </Typography>

            {/* Progress appears only while ordering is locked */}
            {locked && (
              <Box
                sx={{
                  mt: 2,
                  textAlign: "left",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#111",
                    mb: 1,
                  }}
                >
                  {current} of {target} memories captured
                </Typography>

                <Box
                  role="progressbar"
                  aria-label="Memories captured"
                  aria-valuemin={0}
                  aria-valuemax={target}
                  aria-valuenow={Math.min(current, target)}
                  sx={{
                    height: 6,
                    borderRadius: 999,
                    bgcolor: "#e7e7e7",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: `${percentage}%`,
                      height: "100%",
                      bgcolor: "#111",
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: "text.secondary",
                  }}
                >
                  Ordering opens at {target} memories.
                  Keep adding your favorite moments.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Main action */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2.5,
          pt: 1.5,
          pb: "calc(10px + env(safe-area-inset-bottom, 0px))",
          bgcolor: "#fff",
          borderTop: "1px solid #efefef",
        }}
      >
        {checkoutError && (
          <Typography
            role="alert"
            sx={{
              mb: 1.25,
              textAlign: "center",
              color: "error.main",
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {checkoutError}
          </Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          disableElevation
          onClick={
            locked ? toggleFaq : handleCheckout
          }
          disabled={
            busy ||
            (!locked &&
              typeof onCheckout !== "function")
          }
          sx={{
            minHeight: 48,
            px: 2,
            py: 1.25,
            borderRadius: "12px",
            bgcolor: "#111",
            color: "#fff",
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.4,
            textTransform: "none",
            "&:hover": {
              bgcolor: "#262626",
            },
            "&.Mui-disabled": {
              bgcolor: "#111",
              color: "rgba(255,255,255,.65)",
              opacity: 0.65,
            },
          }}
        >
          {busy
            ? "Opening checkout…"
            : locked
              ? showFaq
                ? "Back to photo book"
                : "How it works"
              : `Order my photo book${
                  priceLabel ? ` · ${priceLabel}` : ""
                }`}
        </Button>

        {!locked && (
          <Typography
            sx={{
              mt: 1,
              textAlign: "center",
              fontSize: 11,
              lineHeight: 1.5,
              color: "text.secondary",
            }}
          >
            {shippingLabel}
          </Typography>
        )}

        {!locked && (
          <Button
            fullWidth
            onClick={toggleFaq}
            sx={{
              mt: 0.25,
              minHeight: 44,
              borderRadius: 2,
              color: "#666",
              textTransform: "none",
              fontSize: 13,
              fontWeight: 400,
              "&:hover": {
                bgcolor: "#f7f7f7",
              },
            }}
          >
            {showFaq
              ? "Back to photo book"
              : "Questions? View FAQ"}
          </Button>
        )}
      </Box>
    </SwipeableDrawer>
  );
}

export default memo(StorybookDrawer);